/* ==========================================================================
   Lance's Cool Shop — theme.js
   Minimal vanilla JS. No frameworks, no dependencies.
   Handles: mobile menu, cart drawer, quick add, product variants,
   quantity steppers, sticky mobile add-to-cart, cart page, sort.
   ========================================================================== */
(function () {
  'use strict';

  var T = window.theme || {};
  var routes = T.routes || {};
  var strings = T.strings || {};

  /* ----------------------------- Helpers ------------------------------ */

  function $(sel, ctx) { return (ctx || document).querySelector(sel); }
  function $all(sel, ctx) { return Array.prototype.slice.call((ctx || document).querySelectorAll(sel)); }

  function fetchJSON(url, options) {
    options = options || {};
    options.headers = Object.assign({ 'Accept': 'application/json' }, options.headers || {});
    return fetch(url, options).then(function (r) {
      return r.json().then(function (data) {
        if (!r.ok) { throw data; }
        return data;
      });
    });
  }

  // Standard Shopify money formatter (handles ${{amount}}, {{amount_no_decimals}}, etc.)
  function formatMoney(cents, format) {
    if (typeof cents === 'string') { cents = cents.replace('.', ''); }
    format = format || T.moneyFormat || '${{amount}}';
    var value = '';
    var placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;

    function withCommas(number) {
      return number.replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1,');
    }
    function defaultTo(num) {
      var amount = (cents / 100.0).toFixed(num);
      var parts = amount.split('.');
      var dollars = withCommas(parts[0]);
      return parts[1] ? dollars + '.' + parts[1] : dollars;
    }

    switch ((format.match(placeholderRegex) || [])[1]) {
      case 'amount': value = defaultTo(2); break;
      case 'amount_no_decimals': value = defaultTo(0); break;
      case 'amount_with_comma_separator': value = defaultTo(2).replace('.', ','); break;
      case 'amount_no_decimals_with_comma_separator': value = defaultTo(0); break;
      default: value = defaultTo(2);
    }
    return format.replace(placeholderRegex, value);
  }

  /* --------------------------- Mobile menu ---------------------------- */

  function initMobileMenu() {
    var toggle = $('[data-menu-toggle]');
    var nav = $('[data-mobile-nav]');
    if (!toggle || !nav) { return; }

    toggle.addEventListener('click', function () {
      var open = toggle.getAttribute('aria-expanded') === 'true';
      toggle.setAttribute('aria-expanded', String(!open));
      if (open) {
        nav.hidden = true;
      } else {
        nav.hidden = false;
      }
    });
  }

  /* ---------------------------- Cart drawer --------------------------- */

  var Cart = {
    drawer: null,
    body: null,
    items: null,
    empty: null,
    footer: null,
    subtotal: null,
    lastFocus: null,

    init: function () {
      this.drawer = $('[data-cart-drawer]');
      if (!this.drawer) { return; }
      this.body = $('[data-cart-body]', this.drawer);
      this.items = $('[data-cart-items]', this.drawer);
      this.empty = $('[data-cart-empty]', this.drawer);
      this.footer = $('[data-cart-footer]', this.drawer);
      this.subtotal = $('[data-cart-subtotal]', this.drawer);

      var self = this;

      // Open from header cart (drawer mode only; otherwise let the link work)
      $all('[data-cart-toggle]').forEach(function (el) {
        el.addEventListener('click', function (e) {
          if (T.cartType === 'drawer') {
            e.preventDefault();
            self.refresh().then(function () { self.open(); });
          }
        });
      });

      // Close handlers
      $all('[data-cart-close]', this.drawer).forEach(function (el) {
        el.addEventListener('click', function () { self.close(); });
      });
      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' && self.drawer.classList.contains('is-open')) { self.close(); }
      });

      // Delegated qty / remove inside the drawer
      this.items.addEventListener('click', function (e) {
        var line = e.target.closest('[data-line]');
        if (!line) { return; }
        var key = line.getAttribute('data-line-key');
        var qtyEl = $('[data-line-qty]', line);
        var qty = parseInt(qtyEl ? qtyEl.value : '1', 10);

        if (e.target.closest('[data-line-up]')) { self.change(key, qty + 1); }
        else if (e.target.closest('[data-line-down]')) { self.change(key, qty - 1); }
        else if (e.target.closest('[data-line-remove]')) { self.change(key, 0); }
      });
    },

    open: function () {
      this.lastFocus = document.activeElement;
      this.drawer.classList.add('is-open');
      this.drawer.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
      var closeBtn = $('.cart-drawer__close', this.drawer);
      if (closeBtn) { closeBtn.focus(); }
    },

    close: function () {
      this.drawer.classList.remove('is-open');
      this.drawer.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
      if (this.lastFocus) { this.lastFocus.focus(); }
    },

    // Re-fetch the cart and rebuild the drawer + header count
    refresh: function () {
      var self = this;
      return fetchJSON(routes.cart || '/cart.js').then(function (cart) {
        self.render(cart);
        self.updateCount(cart.item_count);
        return cart;
      }).catch(function () { /* keep current UI on failure */ });
    },

    render: function (cart) {
      if (!this.items) { return; }
      this.updateCount(cart.item_count);

      if (cart.item_count === 0) {
        this.items.hidden = true;
        this.items.innerHTML = '';
        if (this.empty) { this.empty.hidden = false; }
        if (this.footer) { this.footer.hidden = true; }
        return;
      }

      if (this.empty) { this.empty.hidden = true; }
      this.items.hidden = false;
      if (this.footer) { this.footer.hidden = false; }
      if (this.subtotal) { this.subtotal.textContent = formatMoney(cart.total_price); }

      var html = cart.items.map(function (item) {
        var img = item.image
          ? '<img src="' + item.image.replace(/(\.[a-z]+)(\?.*)?$/i, '_160x$1') + '" alt="" loading="lazy">'
          : '';
        var variant = item.product_has_only_default_variant
          ? ''
          : '<p class="drawer-item__variant">' + escapeHtml(item.variant_title || '') + '</p>';
        return (
          '<li class="drawer-item" data-line data-line-key="' + item.key + '">' +
            '<a class="drawer-item__media" href="' + item.url + '" data-cart-close>' + img + '</a>' +
            '<div class="drawer-item__details">' +
              '<a class="drawer-item__title" href="' + item.url + '" data-cart-close>' + escapeHtml(item.product_title) + '</a>' +
              variant +
              '<div class="qty qty--sm">' +
                '<button type="button" class="qty__btn" data-line-down aria-label="Decrease">&minus;</button>' +
                '<input class="qty__input" type="number" min="0" value="' + item.quantity + '" data-line-qty aria-label="Quantity">' +
                '<button type="button" class="qty__btn" data-line-up aria-label="Increase">&plus;</button>' +
              '</div>' +
              '<button type="button" class="drawer-item__remove" data-line-remove>Remove</button>' +
            '</div>' +
            '<div class="drawer-item__price">' + formatMoney(item.final_line_price) + '</div>' +
          '</li>'
        );
      }).join('');

      this.items.innerHTML = html;

      // Re-bind close on freshly rendered links
      var self = this;
      $all('[data-cart-close]', this.items).forEach(function (el) {
        el.addEventListener('click', function () { self.close(); });
      });
    },

    // Add a variant by id
    add: function (variantId, quantity, button) {
      var self = this;
      if (button) { button.classList.add('is-loading'); }
      return fetchJSON(routes.cartAdd || '/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: variantId, quantity: quantity || 1 })
      }).then(function () {
        cartPop();
        if (T.cartType === 'drawer') {
          return self.refresh().then(function () { self.open(); });
        }
        // Page mode: go to the cart page
        window.location.href = routes.cart || '/cart';
      }).catch(function (err) {
        var msg = (err && err.description) ? err.description : 'Sorry, that could not be added.';
        alert(msg);
      }).finally(function () {
        if (button) { button.classList.remove('is-loading'); }
      });
    },

    // Change quantity for a line key (used by drawer)
    change: function (key, quantity) {
      var self = this;
      this.body.classList.add('is-loading');
      return fetchJSON(routes.cartChange || '/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: key, quantity: quantity })
      }).then(function (cart) {
        self.render(cart);
      }).catch(function () {}).finally(function () {
        self.body.classList.remove('is-loading');
      });
    },

    updateCount: function (count) {
      $all('[data-cart-count]').forEach(function (el) {
        var prev = parseInt(el.textContent, 10) || 0;
        el.textContent = count;
        el.classList.toggle('is-empty', count === 0);
        if (count > prev) {
          el.classList.remove('bump');
          // force reflow so the animation can replay
          void el.offsetWidth;
          el.classList.add('bump');
        }
      });
    }
  };

  function escapeHtml(str) {
    return String(str)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  /* ---------------------------- Quick add ----------------------------- */

  function initQuickAdd() {
    $all('[data-quick-add]').forEach(function (btn) {
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        var id = btn.getAttribute('data-variant-id');
        if (id) { Cart.add(id, 1, btn); }
      });
    });
  }

  /* -------------------------- Product page ---------------------------- */

  function initProduct() {
    var section = $('[data-product-section]');
    if (!section) { return; }

    var jsonEl = $('[data-product-json]', section);
    var variants = [];
    try { variants = JSON.parse(jsonEl.textContent); } catch (e) {}

    var form = $('[data-product-form]', section);
    var variantInput = $('[data-variant-input]', section);
    var priceTarget = $('[data-price-target]', section);
    var atcButton = $('[data-atc-button]', section);
    var atcText = $('[data-atc-text]', section);
    var optionInputs = $all('[data-option-input]', section);

    // ---- Quantity steppers (PDP) ----
    bindQty(section);

    // ---- Variant selection ----
    function getSelectedVariant() {
      if (!optionInputs.length) { return variants[0]; }
      var selected = [];
      // Gather one checked value per option position, in order
      var positions = {};
      optionInputs.forEach(function (input) {
        var pos = parseInt(input.getAttribute('data-option-input'), 10);
        if (input.checked) { positions[pos] = input.value; }
      });
      Object.keys(positions).sort(function (a, b) { return a - b; }).forEach(function (pos) {
        selected.push(positions[pos]);
      });
      return variants.find(function (v) {
        return v.options.length === selected.length && v.options.every(function (val, i) {
          return val === selected[i];
        });
      });
    }

    function updateSelectedLabels() {
      optionInputs.forEach(function (input) {
        if (!input.checked) { return; }
        var pos = input.getAttribute('data-option-input');
        var label = $('[data-option-selected="' + pos + '"]', section);
        if (label) { label.textContent = input.value; }
      });
    }

    function switchMedia(variant) {
      if (!variant || !variant.featured_media) { return; }
      var mediaId = String(variant.featured_media.id);
      $all('[data-media-id]', section).forEach(function (slide) {
        var active = slide.getAttribute('data-media-id') === mediaId;
        slide.classList.toggle('is-active', active);
        slide.hidden = !active;
      });
      $all('[data-media-thumb]', section).forEach(function (thumb) {
        thumb.classList.toggle('is-active', thumb.getAttribute('data-media-thumb') === mediaId);
      });
    }

    function updateUI() {
      updateSelectedLabels();
      var variant = getSelectedVariant();

      if (!variant) {
        if (atcButton) { atcButton.disabled = true; }
        if (atcText) { atcText.textContent = 'Unavailable'; }
        return;
      }

      if (variantInput) { variantInput.value = variant.id; }

      // Price
      if (priceTarget) {
        var html = '<div class="price' + (variant.compare_at_price > variant.price ? ' price--on-sale' : '') + '">';
        if (variant.compare_at_price > variant.price) {
          html += '<span class="price__current">' + formatMoney(variant.price) + '</span>';
          html += '<s class="price__compare">' + formatMoney(variant.compare_at_price) + '</s>';
          html += '<span class="price__pill">Sale</span>';
        } else {
          html += '<span class="price__current">' + formatMoney(variant.price) + '</span>';
        }
        html += '</div>';
        priceTarget.innerHTML = html;
      }

      // Availability
      if (atcButton) {
        atcButton.disabled = !variant.available;
        if (atcText) { atcText.textContent = variant.available ? (strings.addToCart || 'Add to cart') : (strings.soldOut || 'Sold out'); }
      }

      // Sticky bar
      var stickyPrice = $('[data-sticky-price]');
      if (stickyPrice) { stickyPrice.textContent = formatMoney(variant.price); }
      var stickyAdd = $('[data-sticky-add]');
      if (stickyAdd) {
        stickyAdd.disabled = !variant.available;
        stickyAdd.textContent = variant.available ? 'Add to cart' : 'Sold out';
      }

      switchMedia(variant);

      // Reflect variant in URL without reloading
      if (history.replaceState && variant.id) {
        var url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        history.replaceState({}, '', url.toString());
      }
    }

    optionInputs.forEach(function (input) {
      input.addEventListener('change', updateUI);
    });

    // ---- Thumbnails ----
    $all('[data-media-thumb]', section).forEach(function (thumb) {
      thumb.addEventListener('click', function () {
        var id = thumb.getAttribute('data-media-thumb');
        $all('[data-media-id]', section).forEach(function (slide) {
          var active = slide.getAttribute('data-media-id') === id;
          slide.classList.toggle('is-active', active);
          slide.hidden = !active;
        });
        $all('[data-media-thumb]', section).forEach(function (t) {
          t.classList.toggle('is-active', t === thumb);
        });
      });
    });

    // ---- AJAX add to cart on submit ----
    if (form) {
      form.addEventListener('submit', function (e) {
        e.preventDefault();
        var id = variantInput ? variantInput.value : null;
        var qtyEl = $('[data-qty-input]', section);
        var qty = qtyEl ? parseInt(qtyEl.value, 10) || 1 : 1;
        if (id) {
          if (atcText) { atcText.textContent = strings.adding || 'Adding…'; }
          Cart.add(id, qty, atcButton).then(function () {
            if (atcText) { atcText.textContent = strings.added || 'Added \u2713'; }
            if (atcButton) {
              atcButton.classList.remove('is-added');
              void atcButton.offsetWidth;
              atcButton.classList.add('is-added');
            }
            setTimeout(function () {
              if (atcText) { atcText.textContent = strings.addToCart || 'Add to cart'; }
            }, 1400);
          });
        }
      });
    }

    // ---- Sticky mobile ATC ----
    initStickyATC(form);

    // Initialize once
    updateUI();
  }

  function initStickyATC(form) {
    var bar = $('[data-sticky-atc]');
    var addBtn = $('[data-sticky-add]');
    var anchor = $('[data-atc-button]');
    if (!bar) { return; }

    bar.hidden = false;

    if (addBtn && form) {
      addBtn.addEventListener('click', function () {
        if (typeof form.requestSubmit === 'function') { form.requestSubmit(); }
        else { form.dispatchEvent(new Event('submit', { cancelable: true })); }
      });
    }

    if (!anchor || !('IntersectionObserver' in window)) { return; }
    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        // Show the bar only when the main button has scrolled out of view
        bar.classList.toggle('is-visible', !entry.isIntersecting);
      });
    }, { rootMargin: '0px 0px -10% 0px' });
    observer.observe(anchor);
  }

  /* ---------------------- Quantity stepper (generic) ------------------ */

  function bindQty(ctx) {
    var input = $('[data-qty-input]', ctx);
    if (!input) { return; }
    var down = $('[data-qty-down]', ctx);
    var up = $('[data-qty-up]', ctx);
    if (down) { down.addEventListener('click', function () {
      input.value = Math.max(1, (parseInt(input.value, 10) || 1) - 1);
    }); }
    if (up) { up.addEventListener('click', function () {
      input.value = (parseInt(input.value, 10) || 1) + 1;
    }); }
  }

  /* ----------------------------- Cart page ---------------------------- */

  function initCartPage() {
    var page = $('[data-cart-page]');
    if (!page) { return; }

    page.addEventListener('click', function (e) {
      var line = e.target.closest('[data-cart-line]');
      if (!line) { return; }
      var key = line.getAttribute('data-line-key');
      var qtyEl = $('[data-line-qty]', line);
      var qty = parseInt(qtyEl ? qtyEl.value : '1', 10);

      if (e.target.closest('[data-line-up]')) { updateLine(key, qty + 1); }
      else if (e.target.closest('[data-line-down]')) { updateLine(key, Math.max(0, qty - 1)); }
      else if (e.target.closest('[data-line-remove]')) { updateLine(key, 0); }
    });

    page.addEventListener('change', function (e) {
      if (e.target.matches('[data-line-qty]')) {
        var line = e.target.closest('[data-cart-line]');
        updateLine(line.getAttribute('data-line-key'), parseInt(e.target.value, 10) || 0);
      }
    });

    function updateLine(key, quantity) {
      page.classList.add('is-loading');
      fetchJSON(routes.cartChange || '/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: key, quantity: quantity })
      }).then(function () {
        // Simplest robust update: reload to re-render line prices/subtotal
        window.location.reload();
      }).catch(function () {
        page.classList.remove('is-loading');
      });
    }
  }

  /* ------------------------------ Sort -------------------------------- */

  function initSort() {
    var select = $('[data-sort-select]');
    if (!select) { return; }
    select.addEventListener('change', function () {
      var url = new URL(window.location.href);
      url.searchParams.set('sort_by', select.value);
      url.searchParams.delete('page');
      window.location.href = url.toString();
    });
  }

  /* --------------------------- Scroll reveal -------------------------- */

  function initReveal() {
    var els = $all('.reveal');
    if (!els.length) return;

    var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduce || !('IntersectionObserver' in window)) {
      els.forEach(function (el) { el.classList.add('is-visible'); });
      return;
    }

    var obs = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          obs.unobserve(entry.target);
        }
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.05 });

    els.forEach(function (el) { obs.observe(el); });
  }

  /* --------------------------- Back to top ---------------------------- */

  function initBackToTop() {
    var btn = $('[data-to-top]');
    if (!btn) return;
    var shown = false;
    function onScroll() {
      var show = window.pageYOffset > 600;
      if (show !== shown) {
        shown = show;
        btn.classList.toggle('is-visible', show);
      }
    }
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    btn.addEventListener('click', function () {
      var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      window.scrollTo({ top: 0, behavior: reduce ? 'auto' : 'smooth' });
    });
  }

  /* ------------------------ Delight: cart "+1" ------------------------ */

  function cartPop() {
    var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduce) return;
    var anchor = $('[data-cart-toggle]') || $('.header__cart');
    if (!anchor) return;
    var rect = anchor.getBoundingClientRect();
    var pop = document.createElement('div');
    pop.className = 'cart-pop';
    pop.textContent = '+1';
    pop.style.left = (rect.left + rect.width / 2 - 12) + 'px';
    pop.style.top = (rect.top + 2) + 'px';
    document.body.appendChild(pop);
    setTimeout(function () { if (pop.parentNode) pop.parentNode.removeChild(pop); }, 950);
  }

  /* --------------------- Delight: random glow pick -------------------- */

  function initRandomGlow() {
    // Only on pages with a real product grid (not placeholders)
    var cards = $all('.product-grid .card:not(.card--placeholder)');
    if (cards.length < 3) return; // need a few to make it feel special

    var pick = cards[Math.floor(Math.random() * cards.length)];
    var media = $('.card__media', pick);
    if (!media) return;

    pick.classList.add('card--glow');
    var tag = document.createElement('span');
    tag.className = 'card__glow-tag';
    tag.innerHTML = '<span class="spark">\u2728</span> Lance\u2019s random pick';
    media.appendChild(tag);
  }

  /* ------------------------------ Boot -------------------------------- */

  function boot() {
    initMobileMenu();
    Cart.init();
    initQuickAdd();
    initProduct();
    initCartPage();
    initSort();
    initReveal();
    initBackToTop();
    initRandomGlow();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
